import type { ParsedUrlQuery } from 'querystring';

import { isAdvancedCheckoutQuery, isAdvancedOrderType } from '@/constants/advancedOrder';

import { type ChainAddress, isValidAddressForChain } from './models';
import { isUsdStableAddress } from './multichain';
import { normalizeTokenAddress, tokenKey } from './normalizeAddress';
import { routes } from './routes';
import { type DefaultTokenPair, getDefaultPairForChain, resolveDefaultTokenPair } from './swap';
import type { TokenInfo } from './token.types';
import {
  getTokenInfoFromQueryParameter,
  parseNetworkSegment,
  parseTokenPath,
  tradePathSegments,
  tradeRoute,
  tradeRouteHref,
  tradeRoutePathname,
  tradeTypeFromPathname,
  type TokenRef,
  type TradeType,
} from './url';

export type TradeRouteTokens = DefaultTokenPair & {
  chartToken: TokenRef | undefined;
};

export type TradeRouteState = TradeRouteTokens & {
  tradeType: TradeType;
};

export type TradePath = {
  tradeType: TradeType;
  chainId?: number;
  token?: TokenInfo;
};

type RedirectRequest = { type: 'redirect'; destination: string };
type RenderRequest = { type: 'render'; state: TradeRouteState };
type NotFoundRequest = { type: 'not-found' };

const PAIR_QUERY_KEYS = ['buyAddress', 'buyChain', 'sellAddress', 'sellChain'];
const ROUTE_PARAM_KEYS = ['tradePath', 'chainId', 'contractAddresses'];
const LEGACY_MODE_KEYS = ['mode', 'orderType'];

export const isSameToken = (a: TokenRef | undefined, b: TokenRef | undefined): boolean =>
  !!a && !!b && tokenKey(a.address, a.chainId) === tokenKey(b.address, b.chainId);

const pairSide = (address: string | undefined, chainId: number | undefined) =>
  address && chainId ? { address, chainId } : undefined;

export const pickChartToken = (
  pair: DefaultTokenPair,
  current?: TokenRef,
): TokenRef | undefined => {
  const buy = pairSide(pair.buyTokenAddress, pair.buyTokenChainId);
  const sell = pairSide(pair.sellTokenAddress, pair.sellTokenChainId);
  if (isSameToken(current, buy) || isSameToken(current, sell)) return current;
  // Prefer a non-stablecoin because its chart shows more price movement.
  if (buy && !isUsdStableAddress(buy.address, buy.chainId)) return buy;
  return sell ?? buy;
};

const readSide = (query: ParsedUrlQuery, side: 'buy' | 'sell') => {
  const token = getTokenInfoFromQueryParameter(query[`${side}Address`], query[`${side}Chain`]);
  return (
    token && {
      address: normalizeTokenAddress(token.address, token.chainId) as ChainAddress,
      chainId: token.chainId,
    }
  );
};

/** Resolves the pair and chart token from the query plus an optional path network or token. */
export const resolveTradeTokens = (
  query: ParsedUrlQuery,
  path: Pick<TradePath, 'chainId' | 'token'> = {},
): TradeRouteTokens => {
  const { token: pathToken, chainId: networkChainId } = path;
  let buy = readSide(query, 'buy');
  let sell = readSide(query, 'sell');
  if (pathToken && !isSameToken(pathToken, buy) && !isSameToken(pathToken, sell)) {
    if (!buy) buy = pathToken;
    else if (!sell) sell = pathToken;
    else buy = pathToken;
  } else if (!pathToken && networkChainId !== undefined && !buy && !sell) {
    const { buyAddress, sellAddress } = getDefaultPairForChain(networkChainId);
    buy = { address: buyAddress, chainId: networkChainId };
    sell = { address: sellAddress, chainId: networkChainId };
  }

  const pair = resolveDefaultTokenPair({
    buyTokenAddress: buy?.address,
    buyTokenChainId: buy?.chainId,
    sellTokenAddress: sell?.address,
    sellTokenChainId: sell?.chainId,
  });

  return { ...pair, chartToken: pathToken ?? pickChartToken(pair) };
};

/** Parses `/{tradeType}/{network?}/{token?}`; undefined for any invalid or extra segment. */
export const parseTradePath = (
  tradeType: TradeType,
  tradePath: string | string[] | undefined,
): TradePath | undefined => {
  const segments = [tradePath ?? []].flat();
  if (segments.length > 2) return undefined;

  const [network, tokenSegment] = segments;
  if (network === undefined) return { tradeType };
  const chainId = parseNetworkSegment(network);
  if (chainId === undefined) return undefined;
  if (tokenSegment === undefined) return { tradeType, chainId };
  const token = parseTokenPath(network, [tokenSegment]);
  return token && { tradeType, chainId, token };
};

/** The trade type named by a legacy `?mode=…&orderType=…` link, if it has one. */
const legacyTradeType = ({ mode, orderType }: ParsedUrlQuery): TradeType | undefined => {
  if (mode === undefined) return undefined;
  if (!isAdvancedCheckoutQuery(mode)) return 'swap';
  return isAdvancedOrderType(orderType) ? orderType : 'limit';
};

const omitKeys = (query: ParsedUrlQuery, keys: string[]) =>
  Object.fromEntries(Object.entries(query).filter(([key]) => !keys.includes(key)));

const pairQuery = (pair: DefaultTokenPair) => ({
  ...(pair.buyTokenAddress && { buyAddress: pair.buyTokenAddress }),
  ...(pair.buyTokenChainId && { buyChain: String(pair.buyTokenChainId) }),
  ...(pair.sellTokenAddress && { sellAddress: pair.sellTokenAddress }),
  ...(pair.sellTokenChainId && { sellChain: String(pair.sellTokenChainId) }),
});

const redirectTo = (
  { tradeType, chartToken }: TradeRouteState,
  query: ParsedUrlQuery,
): RedirectRequest | undefined => {
  if (!chartToken || !isValidAddressForChain(chartToken.address, chartToken.chainId)) {
    return undefined;
  }
  const route = tradeRoute(chartToken, tradeType);
  const search = omitKeys(query, [...ROUTE_PARAM_KEYS, ...LEGACY_MODE_KEYS]);
  return {
    type: 'redirect',
    destination: tradeRouteHref({ ...route, query: { ...search, ...route.query } }),
  };
};

/** Bare `/` renders the default pair; a pair or legacy mode query redirects to its trade route. */
export const resolveHomepageRequest = (query: ParsedUrlQuery): RedirectRequest | RenderRequest => {
  const legacyType = legacyTradeType(query);
  const state = { tradeType: legacyType ?? 'swap', ...resolveTradeTokens(query) };
  const hasPair = !!readSide(query, 'buy') || !!readSide(query, 'sell');
  const redirect = (hasPair || legacyType) && redirectTo(state, query);
  return redirect || { type: 'render', state };
};

/** Legacy `/tokens/{network}/{token}` links redirect to the token's trade route. */
export const resolveTokenRequest = (query: ParsedUrlQuery): RedirectRequest | NotFoundRequest => {
  const token = parseTokenPath(query.chainId, query.contractAddresses);
  if (!token) return { type: 'not-found' };
  const state = {
    tradeType: legacyTradeType(query) ?? 'swap',
    ...resolveTradeTokens(query, { chainId: token.chainId, token }),
  };
  return redirectTo(state, query) ?? { type: 'not-found' };
};

/** `/{tradeType}/{network?}/{token?}`; omitted segments render their defaults in place. */
export const resolveTradeTypeRequest = (
  tradeType: TradeType,
  query: ParsedUrlQuery,
): RedirectRequest | RenderRequest | NotFoundRequest => {
  const path = parseTradePath(tradeType, query.tradePath);
  if (!path) return { type: 'not-found' };
  const legacyType = legacyTradeType(query);
  // Only bare `/swap` carried a mode query before the path named it.
  const isLegacy = tradeType === 'swap' && path.chainId === undefined && !!legacyType;
  const state = {
    tradeType: (isLegacy && legacyType) || tradeType,
    ...resolveTradeTokens(query, path),
  };
  return (isLegacy && redirectTo(state, query)) || { type: 'render', state };
};

/** Resolves the rendered homepage or trade route state on the client. */
export const resolveTradeRoute = (
  pathname: string,
  query: ParsedUrlQuery,
): TradeRouteState | undefined => {
  if (pathname === routes.INDEX) {
    return { tradeType: 'swap', ...resolveTradeTokens(query) };
  }
  const tradeType = tradeTypeFromPathname(pathname);
  if (!tradeType) return undefined;
  const path = parseTradePath(tradeType, query.tradePath);
  return path && { tradeType, ...resolveTradeTokens(query, path) };
};

/**
 * The full route for a trade state. Keeps unrelated query parameters and the
 * typed token segment (e.g. a native symbol) while the chart token is unchanged.
 */
export const fullTradeRoute = (state: TradeRouteState, query: ParsedUrlQuery) => {
  const { tradeType, chartToken } = state;
  const currentPath = parseTradePath(tradeType, query.tradePath);
  const tradePath =
    Array.isArray(query.tradePath) && isSameToken(currentPath?.token, chartToken)
      ? query.tradePath
      : chartToken
        ? tradePathSegments(chartToken)
        : [];

  return {
    pathname: tradeRoutePathname(tradeType),
    query: {
      ...omitKeys(query, [...PAIR_QUERY_KEYS, ...ROUTE_PARAM_KEYS, ...LEGACY_MODE_KEYS]),
      ...pairQuery(state),
      tradePath,
    },
  };
};

/** Full route for an order-specific pair link, charting the pair's preferred token. */
export const tradeRouteForPair = (pair: DefaultTokenPair, tradeType: TradeType) =>
  fullTradeRoute({ ...pair, tradeType, chartToken: pickChartToken(pair) }, {});
